import map from 'lodash/map';
import isPlainObject from 'lodash/isPlainObject';
import { checkSchema } from '@zedoc/check-schema';
import {
  estimateTheta,
  getParamsFromConfig,
  getParamsByValueOrder,
  getStandardError,
  applyClinicalScoreMap,
  hasNegativeAlphaItems,
  detectCalibrationClass,
  extractRSMParams,
  computeTScore,
  scoreNormedMean,
} from '@zedoc/cat-irt';
import Formula from '../Formula';
import { FORMULA_TYPE__IRT_CALCULATION } from '../../../constants';
import { isEmptyAnswer } from '../../../utils/question';

const settingsSchema = {
  type: 'object',
  required: ['property', 'config', 'operands'],
  properties: {
    property: {
      // tScore   — IRT-based clinical T-score (theta-driven).
      // normedT  — culturally-normed mean-of-responses T-score (SHAW-CAT
      //            style), uses settings.sectionNorm = { mu, sigma }.
      enum: ['theta', 'standardError', 'tScore', 'normedT'],
    },
    method: {
      enum: ['BM', 'EAP', 'ML'],
    },
    clipTScore: {
      type: 'boolean',
    },
    // SHAW-CAT pipeline opt-ins (PROMIS-default behaviour preserved if unset).
    responseScoreMap: {
      enum: ['NATIVE', 'CLINICAL'],
    },
    parameterOrdering: {
      enum: ['BY_BETA', 'BY_VALUE'],
    },
    tScoreProfile: {
      enum: ['PROMIS', 'PROMIN', 'none'],
    },
    // Per-section normative reference, used only when property === 'normedT'.
    sectionNorm: {
      type: 'object',
      required: ['mu', 'sigma'],
      properties: {
        mu: { type: 'number' },
        sigma: { type: 'number' },
      },
    },
    config: {
      type: 'object',
    },
    operands: {
      type: 'array',
      items: {
        type: 'object',
        required: ['id'],
        properties: {
          id: {
            type: 'string',
          },
        },
      },
    },
  },
};

// catR / PROMIS T-score convention: T = 50 + 10·θ, then clip to [1, 99].
// Matches Promin display behaviour observed in the SHAW-CAT spreadsheet.
const T_SCORE_MIN = 1;
const T_SCORE_MAX = 99;

class FormulaIRTCalculation extends Formula {
  validate() {
    if (!this.settings) {
      return this.constructor.NotConfigured;
    }
    if (checkSchema(settingsSchema, this.settings)) {
      return this.constructor.NotConfigured;
    }
    if (this.settings.property === 'normedT' && !this.settings.sectionNorm) {
      return this.constructor.NotConfigured;
    }
    return undefined;
  }

  evaluate(scope) {
    const config = Formula.create(this.settings.config).evaluate(scope);
    if (config.error) {
      return {
        error: config.error,
      };
    }
    if (!isPlainObject(config.value)) {
      return {
        error: this.constructor.NoData,
      };
    }
    // Auto-detect calibration class:
    //   RSM (Andrich)  → SHAW-CAT (MLE + PROMIN clip); δ/τ from extractRSMParams.
    //   GRM (Samejima) → PROMIS canonical (BM + NATIVE + BY_BETA, no clip)
    //                    or legacy 2PL SHAW-CAT (CLINICAL + BY_VALUE + ML + PROMIN clip)
    //                    if any α<0 items are present.
    // Settings override auto-detect so authoring can force any behaviour.
    const calibrationClass = detectCalibrationClass(config.value);
    const isShawCatStyle = hasNegativeAlphaItems(config.value);
    const tScoreProfile =
      this.settings.tScoreProfile ||
      (calibrationClass === 'RSM' || isShawCatStyle ? 'PROMIN' : 'PROMIS');

    const rawResponses = this.settings.operands.flatMap(({ id }) => {
      const answer = scope.lookupAnswer(id);
      if (!isEmptyAnswer(answer)) {
        return [
          {
            questionId: id,
            answer,
          },
        ];
      }
      return [];
    });

    let params;
    let responses;
    let method;
    if (calibrationClass === 'RSM') {
      // RSM path. MLE end-to-end (matching paper Kwan et al. 2024).
      // Direction handling is in the calibration's score field — no α-flip.
      //
      // review:manual: extractRSMParams throws on malformed items
      // (missing/non-numeric alpha, <2 coefficients, empty τ vector,
      // non-numeric score). Formula evaluation on a partially-authored
      // RSM calibration will surface as a hard crash in the caller
      // instead of a NoData error, so a form-builder preview mid-edit
      // could break rendering. Prod configs are safe (checked-in JSON,
      // all-or-nothing load). Wrap in try/catch → NoData once RSM
      // authoring is opened to end-users; keeping the hard-fail here
      // now because the throw is diagnostic value for developers.
      params = extractRSMParams(config.value);
      responses = rawResponses;
      method = this.settings.method || 'ML';
    } else {
      // GRM path — legacy 2PL.
      const parameterOrdering =
        this.settings.parameterOrdering ||
        (isShawCatStyle ? 'BY_VALUE' : 'BY_BETA');
      const responseScoreMap =
        this.settings.responseScoreMap ||
        (isShawCatStyle ? 'CLINICAL' : 'NATIVE');
      method = this.settings.method || (isShawCatStyle ? 'ML' : 'BM');
      params =
        parameterOrdering === 'BY_VALUE'
          ? getParamsByValueOrder(config.value)
          : getParamsFromConfig(config.value);
      responses =
        responseScoreMap === 'CLINICAL'
          ? applyClinicalScoreMap(rawResponses, config.value)
          : rawResponses;
    }
    // Profiles: PROMIS = no clip, PROMIN = clip [1,99], none = no clip.
    // Legacy clipTScore boolean still respected when tScoreProfile is unset.
    const clipTScore =
      this.settings.tScoreProfile != null
        ? tScoreProfile === 'PROMIN'
        : this.settings.clipTScore !== false;
    let value;
    switch (this.settings.property) {
      case 'theta': {
        value = estimateTheta(params, responses, { method });
        break;
      }
      case 'standardError': {
        // For GRM (PROMIS), SE uses BM regardless of the method picked for
        // theta/T-score — ML SE at saturation collapses (Fisher info ≈ 0 →
        // SE → ∞), so it's useless as a reportable precision metric. BM SE
        // adds prior precision and reflects the same value the live CAT loop
        // used to decide stopping.
        //
        // For RSM (SHAW-CAT, post-23-May), SE uses MLE end-to-end per Singapore
        // — saturation behaviour at extremes is intentional and matches the
        // published Table 3 method.
        const seMethod = calibrationClass === 'RSM' ? 'ML' : 'BM';
        const theta = estimateTheta(params, responses, { method: seMethod });
        value = getStandardError(params, responses, theta, {
          method: seMethod,
        });
        break;
      }
      case 'tScore': {
        const theta = estimateTheta(params, responses, { method });
        // Use the shared computeTScore helper so the clip convention matches
        // Promin's PROMIN profile exactly (1..99 inclusive). Legacy
        // `clipTScore` falls back to PROMIS/PROMIN equivalents.
        const profile =
          this.settings.tScoreProfile != null
            ? tScoreProfile
            : clipTScore
            ? 'PROMIN'
            : 'PROMIS';
        value = computeTScore(theta, profile);
        break;
      }
      case 'normedT': {
        // Culturally-normed mean-of-responses T-score (SHAW-CAT style):
        //   T = clip([1, 99], 50 + 10 × (mean_response − μ) / σ)
        // Uses the per-section (μ, σ) supplied in settings.sectionNorm.
        //
        // For RSM: direction handling is in the calibration's score field,
        // so no α-flip is needed — use rawResponses as-is.
        // For GRM with α<0: apply the clinical score-map so all-best ↔
        // all-worst behave symmetrically regardless of the bank's α-sign
        // convention.
        //
        // review:manual: scoreNormedMean returns null when there are no
        // usable responses, and unlike the theta/standardError/tScore
        // paths this case doesn't guard the assignment explicitly. It
        // relies on the tail `if (Number.isFinite(value))` check (below,
        // after the switch) — since `Number.isFinite(null) === false`,
        // null already flows to `return { error: this.constructor.NoData }`
        // exactly like the other paths. Kept as MANUAL because prior
        // review passes flagged it as a hard issue when the tail was
        // out of view (diff truncation); leaving this note so future
        // truncated passes don't re-flag it. If you prefer an explicit
        // guard, replace the assignment with:
        //   const raw = scoreNormedMean(...); value = raw ?? undefined;
        // (undefined trips the same Number.isFinite → NoData path).
        let adjusted;
        if (calibrationClass === 'RSM') {
          adjusted = rawResponses;
        } else {
          const grmScoreMap =
            this.settings.responseScoreMap ||
            (isShawCatStyle ? 'CLINICAL' : 'NATIVE');
          adjusted =
            grmScoreMap === 'CLINICAL'
              ? applyClinicalScoreMap(rawResponses, config.value)
              : rawResponses;
        }
        value = scoreNormedMean(adjusted, this.settings.sectionNorm);
        break;
      }
      default: {
        // ...
      }
    }
    if (Number.isFinite(value)) {
      return {
        value,
      };
    }
    return {
      error: this.constructor.NoData,
    };
  }

  compile(questionsHierarchy) {
    const compiled = {
      ...this,
      settings: {
        property: this.settings.property,
        ...(this.settings.method !== undefined && {
          method: this.settings.method,
        }),
        ...(this.settings.clipTScore !== undefined && {
          clipTScore: this.settings.clipTScore,
        }),
        ...(this.settings.responseScoreMap !== undefined && {
          responseScoreMap: this.settings.responseScoreMap,
        }),
        ...(this.settings.parameterOrdering !== undefined && {
          parameterOrdering: this.settings.parameterOrdering,
        }),
        ...(this.settings.tScoreProfile !== undefined && {
          tScoreProfile: this.settings.tScoreProfile,
        }),
        ...(this.settings.sectionNorm !== undefined && {
          sectionNorm: this.settings.sectionNorm,
        }),
      },
    };
    if (this.meta && this.meta.sectionId) {
      compiled.settings.operands = questionsHierarchy.mapQuestions(
        (q) => ({
          id: q.id,
        }),
        {
          sectionId: this.meta.sectionId,
        },
      );
      const question = questionsHierarchy.getQuestionById(this.meta.sectionId);
      if (question && question.settings && question.settings.irtConfigFormula) {
        compiled.settings.config = Formula.create(
          question.settings.irtConfigFormula,
        ).compile(questionsHierarchy);
      }
    }
    return compiled;
  }

  static createMapSettings(mapQuestionId) {
    return (value, key) => {
      switch (key) {
        case 'config':
          return Formula.create(value).remap(mapQuestionId).toRawFormula();
        case 'operands':
          return map(value, (operand) => ({
            ...operand,
            id: mapQuestionId(operand.id),
          }));
        default:
          return value;
      }
    };
  }

  static createMapMeta(mapQuestionId) {
    return (value, key) => {
      switch (key) {
        case 'sectionId':
          return mapQuestionId(value);
        case 'questionIds':
          return map(value, mapQuestionId);
        default:
          return value;
      }
    };
  }
}

Formula.types[FORMULA_TYPE__IRT_CALCULATION] = FormulaIRTCalculation;

export default FormulaIRTCalculation;
